import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Mail } from "lucide-react";

const CONTACT_EMAIL = "besoindunpro@gmail.com";

export const LEGAL_META = {
  privacy: {
    title: "Politique de confidentialité",
    description: "Politique de confidentialité de l'application Besoin d'un Pro ? : données collectées, utilisation, conservation et suppression.",
    path: "/legal/privacy",
  },
  "data-deletion": {
    title: "Suppression du compte et des données",
    description: "Comment supprimer votre compte et vos données dans l'application Besoin d'un Pro ?",
    path: "/legal/data-deletion",
  },
};

const NAV = [{ key: "privacy" }, { key: "data-deletion" }];

function useLegalSeo(slug) {
  const meta = LEGAL_META[slug];
  useEffect(() => {
    if (!meta) return;
    document.title = `${meta.title} — Besoin d'un Pro ?`;
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content = meta.description;
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.rel = "canonical";
      document.head.appendChild(canon);
    }
    canon.href = `${window.location.origin}${meta.path}`;
  }, [slug, meta]);
}

export default function LegalLayout({ slug, children }) {
  const meta = LEGAL_META[slug];
  const location = useLocation();
  useLegalSeo(slug);

  return (
    <div className="min-h-screen bg-bone text-ink">
      <header className="border-b border-border bg-white sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center gap-3">
          <Link to="/landing" className="flex items-center gap-2 tap-scale" data-testid="legal-header-home">
            <img src="/brand/icon-192.png" alt="Besoin d'un Pro" className="h-8 w-8 rounded-lg" width={32} height={32} />
            <div className="font-heading font-bold text-sm">Besoin d&apos;un Pro&nbsp;?</div>
          </Link>
          <div className="flex-1" />
          <Link to="/landing" className="text-xs text-slate2 hover:text-ink flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour au site
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12 grid md:grid-cols-[220px_1fr] gap-8">
        <aside className="md:sticky md:top-24 md:self-start">
          <div className="text-[10px] uppercase tracking-widest text-slate2 font-semibold mb-2">Pages légales</div>
          <nav className="flex flex-col gap-1" data-testid="legal-sidebar">
            {NAV.map(({ key }) => {
              const m = LEGAL_META[key];
              const active = location.pathname === m.path;
              return (
                <Link
                  key={key}
                  to={m.path}
                  className={`text-sm rounded-lg px-3 py-2 transition ${
                    active ? "bg-pro text-white font-semibold" : "text-ink hover:bg-white"
                  }`}
                  data-testid={`legal-nav-${key}`}
                >
                  {m.title}
                </Link>
              );
            })}
          </nav>
          <div className="mt-6 rounded-2xl bg-white border border-border p-4 text-xs text-slate2">
            <div className="font-semibold text-ink mb-1 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Une question&nbsp;?
            </div>
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline break-all">
              {CONTACT_EMAIL}
            </a>
          </div>
        </aside>

        <article className="prose prose-slate max-w-none">
          <div className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Légal</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1 mb-2 text-ink">{meta?.title}</h1>
          <p className="text-xs text-slate2 mb-8">
            Application&nbsp;: <strong>Besoin d&apos;un Pro&nbsp;?</strong> — Dernière mise à jour&nbsp;: 15 février 2026
          </p>
          <div className="legal-body text-[15px] leading-relaxed space-y-4">{children}</div>
          <div className="mt-12 pt-6 border-t border-border text-xs text-slate2">
            Contact&nbsp;: <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand hover:underline">{CONTACT_EMAIL}</a>
          </div>
        </article>
      </div>
    </div>
  );
}
